import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Image as ImageIcon, Video, Trash2, Pencil, Upload, RefreshCw,
  Loader2, Search, Sparkles, Settings2, FileText, FolderX, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import ProgressiveImg from "@/components/ProgressiveImg";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import IconGalleryGrid from "./IconGalleryGrid";

const API = getApiUrl();
const PAGE_SIZE = 24;

const TYPE_OPTIONS = [
  { v: "all", label: "All types" },
  { v: "images", label: "Images" },
  { v: "videos", label: "Videos" },
  { v: "documents", label: "Documents" },
  { v: "other", label: "Other" },
];

function formatSize(bytes) {
  if (!bytes) return "0 KB";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AssetsAdmin() {
  const [filter, setFilter] = useState("all");
  const [assetType, setAssetType] = useState("all");
  const [unusedOnly, setUnusedOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [missingThumbCount, setMissingThumbCount] = useState(0);
  const [unusedCount, setUnusedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(null);
  const [deletingUnused, setDeletingUnused] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [previewTarget, setPreviewTarget] = useState(null);
  const replaceInputRef = useRef(null);
  const [replaceTarget, setReplaceTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/assets`, {
        params: {
          filter,
          asset_type: assetType,
          unused_only: unusedOnly || undefined,
          search: search || undefined,
          page,
          page_size: PAGE_SIZE,
        },
        withCredentials: true,
      });
      setAssets(data.assets || []);
      setTotal(data.total || 0);
      setMissingThumbCount(data.missing_thumbnail_count || 0);
      setUnusedCount(data.unused_count || 0);
    } catch {
      toast.error("Failed to load assets");
    } finally {
      setLoading(false);
    }
  }, [filter, assetType, unusedOnly, search, page]);

  useEffect(() => { load(); }, [load]);

  const loadSchedule = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/assets/thumbnail-schedule`, { withCredentials: true });
      setSchedule(data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const handleGenerateNow = async () => {
    setGenerating(true);
    const startTotal = missingThumbCount;
    setGenProgress({ done: 0, total: startTotal });
    let totalCreated = 0;
    let totalFailed = 0;
    try {
      // Backend processes a small batch per call (large batches OOM'd the
      // pod in production) — loop until nothing's left or progress stalls.
      let remaining = Infinity;
      let lastRemaining = null;
      let iterations = 0;
      while (remaining > 0 && iterations < 100) {
        const { data } = await axios.post(`${API}/admin/assets/generate-missing-thumbnails`, {}, { withCredentials: true });
        totalCreated += data.created || 0;
        totalFailed += data.failed || 0;
        remaining = data.remaining ?? 0;
        setGenProgress({ done: totalCreated + totalFailed, total: Math.max(startTotal, totalCreated + totalFailed + remaining) });
        if (remaining === lastRemaining) break; // no progress — stop to avoid looping forever
        lastRemaining = remaining;
        iterations += 1;
        setMissingThumbCount(remaining);
      }
      toast.success(`Generated ${totalCreated} thumbnail${totalCreated === 1 ? "" : "s"}${totalFailed ? ` (${totalFailed} failed)` : ""}`);
      load();
      loadSchedule();
    } catch {
      toast.error(`Failed to generate thumbnails${totalCreated ? ` (${totalCreated} completed before the error)` : ""}`);
    } finally {
      setGenerating(false);
      setGenProgress(null);
    }
  };

  const handleScheduleSave = async (updates) => {
    try {
      await axios.put(`${API}/admin/assets/thumbnail-schedule`, updates, { withCredentials: true });
      loadSchedule();
    } catch {
      toast.error("Failed to update schedule");
    }
  };

  const handleSingleThumbnail = async (filename) => {
    try {
      await axios.post(`${API}/admin/assets/${filename}/generate-thumbnail`, {}, { withCredentials: true });
      toast.success("Thumbnail generated");
      load();
    } catch {
      toast.error("Failed to generate thumbnail");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/assets/${deleteTarget.filename}`, { withCredentials: true });
      toast.success("Asset deleted");
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Failed to delete asset");
    }
  };

  const handleRenameSave = async () => {
    if (!renameTarget || !renameValue.trim()) return;
    try {
      await axios.put(`${API}/admin/assets/${renameTarget.filename}/rename`, { name: renameValue.trim() }, { withCredentials: true });
      toast.success("Renamed");
      setRenameTarget(null);
      load();
    } catch {
      toast.error("Failed to rename");
    }
  };

  const handleReplaceFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !replaceTarget) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/admin/assets/${replaceTarget.filename}/replace`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File replaced");
      load();
    } catch {
      toast.error("Failed to replace file");
    } finally {
      setReplaceTarget(null);
    }
  };

  const handleDeleteUnused = async () => {
    setDeletingUnused(true);
    try {
      const { data } = await axios.post(`${API}/admin/assets/delete-unused`, {}, { withCredentials: true });
      toast.success(`Deleted ${data.count} unused asset${data.count === 1 ? "" : "s"}`);
      load();
    } catch {
      toast.error("Failed to delete unused assets");
    } finally {
      setDeletingUnused(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4" data-testid="assets-admin">
      <Tabs defaultValue="media">
        <TabsList data-testid="assets-admin-tabs">
          <TabsTrigger value="media" data-testid="assets-admin-tab-media">Media</TabsTrigger>
          <TabsTrigger value="icons" data-testid="assets-admin-tab-icons">Icons</TabsTrigger>
        </TabsList>

        <TabsContent value="media" className="space-y-4 mt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-serif text-lg font-medium">Assets</h3>
          <p className="text-sm text-muted-foreground">
            All uploaded graphics — property photos, CMS images, logos and their thumbnails.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSchedule(true)}
            data-testid="assets-schedule-btn"
          >
            <Settings2 className="h-4 w-4 mr-1" /> Schedule
          </Button>
          <Button
            size="sm"
            onClick={handleGenerateNow}
            disabled={generating}
            data-testid="assets-generate-now-btn"
          >
            {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
            {generating && genProgress ? (
              <span data-testid="assets-generate-progress">{genProgress.done} of {genProgress.total} done</span>
            ) : (
              <>
                <span className="hidden sm:inline">Generate Missing Thumbnails</span>
                <span className="sm:hidden">Generate Thumbnails</span>
                {missingThumbCount > 0 && ` (${missingThumbCount})`}
              </>
            )}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleDeleteUnused}
            disabled={deletingUnused || unusedCount === 0}
            data-testid="assets-delete-unused-btn"
          >
            {deletingUnused ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FolderX className="h-4 w-4 mr-1" />}
            Delete All Unused {unusedCount > 0 && `(${unusedCount})`}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex border border-border rounded overflow-hidden text-sm">
          {[
            { v: "all", label: "All" },
            { v: "normal", label: "Normal" },
            { v: "thumbnails", label: "Thumbnails" },
          ].map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => { setFilter(opt.v); setPage(1); }}
              className={`px-3 py-1.5 font-medium transition-colors ${filter === opt.v ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
              data-testid={`assets-filter-${opt.v}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <select
          value={assetType}
          onChange={(e) => { setAssetType(e.target.value); setPage(1); }}
          className="h-9 text-sm border border-border rounded px-2 bg-background max-w-[140px]"
          data-testid="assets-type-filter"
        >
          {TYPE_OPTIONS.map((opt) => (
            <option key={opt.v} value={opt.v}>{opt.label}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => { setUnusedOnly((v) => !v); setPage(1); }}
          className={`inline-flex items-center gap-1 h-9 px-3 rounded text-sm font-medium border transition-colors whitespace-nowrap ${unusedOnly ? "bg-amber-500 text-white border-amber-500" : "border-border bg-background hover:bg-muted"}`}
          data-testid="assets-unused-only-toggle"
        >
          <AlertTriangle className="h-3.5 w-3.5" /> Unused only {unusedCount > 0 && `(${unusedCount})`}
        </button>
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by filename..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-8 h-9"
            data-testid="assets-search-input"
          />
        </div>
        <span className="text-xs text-muted-foreground ml-auto">{total} asset{total === 1 ? "" : "s"}</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : assets.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-12" data-testid="assets-empty">No assets found.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="assets-grid">
          {assets.map((a) => (
            <div
              key={a.filename}
              className="border border-border rounded-md overflow-hidden bg-white group relative cursor-pointer"
              data-testid={`asset-card-${a.filename}`}
              onClick={() => setPreviewTarget(a)}
            >
              <div className="aspect-square bg-muted flex items-center justify-center overflow-hidden">
                {a.is_video ? (
                  <Video className="h-8 w-8 text-muted-foreground" />
                ) : a.asset_type === "documents" || a.asset_type === "other" ? (
                  <FileText className="h-8 w-8 text-muted-foreground" />
                ) : (
                  <img
                    src={a.thumbnail_url || a.url}
                    alt={a.original_name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                )}
              </div>
              <div className="p-2 space-y-1">
                <p className="text-xs font-medium truncate" title={a.original_name}>{a.original_name}</p>
                <div className="flex items-center gap-1 flex-wrap">
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{formatSize(a.size)}</Badge>
                  {a.is_thumbnail && <Badge className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700">Thumbnail</Badge>}
                  {!a.is_thumbnail && !a.is_video && (
                    a.thumbnail_filename
                      ? <Badge className="text-[10px] px-1.5 py-0 bg-emerald-100 text-emerald-700">Has thumb</Badge>
                      : <Badge className="text-[10px] px-1.5 py-0 bg-gray-100 text-gray-600">No thumb</Badge>
                  )}
                  {a.is_unused && (
                    <Badge
                      className="text-[10px] px-1.5 py-0 bg-red-100 text-red-700"
                      data-testid={`asset-unused-badge-${a.filename}`}
                    >
                      Unused
                    </Badge>
                  )}
                </div>
              </div>
              <div className="absolute inset-x-0 bottom-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex justify-center gap-1 py-1.5" onClick={(e) => e.stopPropagation()}>
                <button
                  title="Rename"
                  onClick={() => { setRenameTarget(a); setRenameValue(a.original_name); }}
                  className="text-white hover:text-primary p-1"
                  data-testid={`asset-rename-${a.filename}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  title="Replace file"
                  onClick={() => { setReplaceTarget(a); replaceInputRef.current?.click(); }}
                  className="text-white hover:text-primary p-1"
                  data-testid={`asset-replace-${a.filename}`}
                >
                  <Upload className="h-3.5 w-3.5" />
                </button>
                {!a.is_thumbnail && !a.is_video && !a.thumbnail_filename && (
                  <button
                    title="Generate thumbnail"
                    onClick={() => handleSingleThumbnail(a.filename)}
                    className="text-white hover:text-primary p-1"
                    data-testid={`asset-gen-thumb-${a.filename}`}
                  >
                    <ImageIcon className="h-3.5 w-3.5" />
                  </button>
                )}
                {!a.is_thumbnail && !a.is_video && a.thumbnail_filename && (
                  <button
                    title="Regenerate thumbnail"
                    onClick={() => handleSingleThumbnail(a.filename)}
                    className="text-white hover:text-primary p-1"
                    data-testid={`asset-regen-thumb-${a.filename}`}
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  title="Delete"
                  onClick={() => setDeleteTarget(a)}
                  className="text-white hover:text-red-400 p-1"
                  data-testid={`asset-delete-${a.filename}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      )}

      <input
        type="file"
        accept="image/*,video/*"
        ref={replaceInputRef}
        className="hidden"
        onChange={handleReplaceFileChosen}
      />

      {/* Rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="asset-rename-dialog">
          <DialogHeader><DialogTitle>Rename asset</DialogTitle></DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            data-testid="asset-rename-input"
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button onClick={handleRenameSave} data-testid="asset-rename-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full-size preview */}
      <Dialog open={!!previewTarget} onOpenChange={(o) => !o && setPreviewTarget(null)}>
        <DialogContent className="w-[95vw] sm:max-w-3xl" data-testid="asset-preview-dialog">
          <DialogHeader>
            <DialogTitle className="truncate">{previewTarget?.original_name}</DialogTitle>
          </DialogHeader>
          {previewTarget && (
            <div className="space-y-3">
              <div className="flex justify-center bg-muted rounded-md overflow-hidden max-h-[70vh]">
                {previewTarget.is_video ? (
                  <video
                    src={previewTarget.url}
                    controls
                    autoPlay
                    className="max-w-full max-h-[70vh]"
                    data-testid="asset-preview-video"
                  />
                ) : previewTarget.asset_type === "documents" || previewTarget.asset_type === "other" ? (
                  <div className="flex flex-col items-center gap-3 p-10">
                    <FileText className="h-16 w-16 text-muted-foreground" />
                    <a
                      href={previewTarget.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary underline"
                      data-testid="asset-preview-open-document"
                    >
                      Open in new tab
                    </a>
                  </div>
                ) : (
                  <ProgressiveImg
                    src={previewTarget.url}
                    thumbnailSrc={previewTarget.thumbnail_url}
                    alt={previewTarget.original_name}
                    className="max-w-full max-h-[70vh] object-contain"
                    fill={false}
                    data-testid="asset-preview-image"
                  />
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="secondary" className="text-[10px]">{formatSize(previewTarget.size)}</Badge>
                <span>{previewTarget.content_type}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="asset-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this asset?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.original_name}" and its thumbnail (if any) will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="asset-delete-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Thumbnail schedule settings */}
      <Dialog open={showSchedule} onOpenChange={setShowSchedule}>
        <DialogContent className="max-w-md" data-testid="assets-schedule-dialog">
          <DialogHeader><DialogTitle>Thumbnail generation schedule</DialogTitle></DialogHeader>
          {schedule && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="schedule-enabled">Auto-generate on a schedule</Label>
                <Switch
                  id="schedule-enabled"
                  checked={schedule.enabled}
                  onCheckedChange={(v) => { setSchedule({ ...schedule, enabled: v }); handleScheduleSave({ enabled: v }); }}
                  data-testid="assets-schedule-enabled-switch"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="schedule-interval">Run every (minutes)</Label>
                <Input
                  id="schedule-interval"
                  type="number"
                  min={5}
                  value={schedule.interval_minutes}
                  onChange={(e) => setSchedule({ ...schedule, interval_minutes: e.target.value })}
                  onBlur={() => handleScheduleSave({ interval_minutes: Number(schedule.interval_minutes) || 60 })}
                  data-testid="assets-schedule-interval-input"
                />
                <p className="text-xs text-muted-foreground">Minimum 5 minutes. The scheduler checks every 15 minutes and only runs once this interval has elapsed.</p>
              </div>
              <div className="text-xs text-muted-foreground border-t pt-3">
                {schedule.last_run_at ? (
                  <>Last run: {new Date(schedule.last_run_at).toLocaleString("en-GB")} ({schedule.last_run_trigger}) — {schedule.last_run_result?.created || 0} created, {schedule.last_run_result?.failed || 0} failed</>
                ) : (
                  "Never run yet."
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
        </TabsContent>

        <TabsContent value="icons" className="mt-4">
          <IconGalleryGrid />
        </TabsContent>
      </Tabs>
    </div>
  );
}
